import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ScoreAppHeader from '../../../shared/components/layout/ScoreAppHeader';
import Button from '../../../shared/components/ui/Button';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useAllStandingsConfigs, useMatchPointsOperations, useTournamentStandings, useGroupStandings } from '../../../shared/hooks/useMatchPoints';
import { TournamentStandingsConfig } from '../../../shared/services/matchPointsService';
import { useSeasons } from '../../../shared/hooks/useSeasons';
import { useTournaments } from '../../../shared/hooks/useTournaments';
import { matchDetailsService } from '../../../shared/services/matchDetails';
import { supabase } from '../../../shared/lib/supabase';
import TournamentConfigModal from '../components/TournamentConfigModal';
import TieBreakerOrderModal from '../components/TieBreakerOrderModal';
import MatchMappingModal from '../components/MatchMappingModal';
import Toast from '../../../shared/components/ui/Toast';

// Generate dynamic tie-breaker rules based on config
const generateTieBreakerRules = (config: TournamentStandingsConfig) => {
  const pointsText = `Points: Win (${config.default_win_points}), Draw (${config.default_draw_points}), Loss (${config.default_loss_points})`;

  let tieBreakerOrder: string[] = [];
  if (config.tie_breaker_order) {
    if (typeof config.tie_breaker_order === 'string') {
      try {
        tieBreakerOrder = JSON.parse(config.tie_breaker_order);
      } catch (e) {
        tieBreakerOrder = [];
      }
    } else if (Array.isArray(config.tie_breaker_order)) {
      tieBreakerOrder = config.tie_breaker_order;
    }
  }

  const criteriaMap: { [key: string]: string } = {
    'goal_difference': 'goal difference',
    'goals_for': 'goals for',
    'goals_against': 'goals against',
    'head_to_head': 'head to head result',
  };

  const tieBreakerDisplay = tieBreakerOrder.length > 0 
    ? tieBreakerOrder.map(c => criteriaMap[c] || c).join(', ') 
    : 'none';

  return {
    pointsText: pointsText,
    tieBreakerText: `Tie-breaker order: ${tieBreakerDisplay}`
  };
};

const TournamentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const [activeTab, setActiveTab] = useState<'config' | 'points' | 'standings'>('config');
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<TournamentStandingsConfig | null>(null);
  const [isTieBreakerModalOpen, setIsTieBreakerModalOpen] = useState(false);
  const [tieBreakerConfig, setTieBreakerConfig] = useState<TournamentStandingsConfig | null>(null);
  const [isMatchMappingModalOpen, setIsMatchMappingModalOpen] = useState(false);
  const [matchMappingConfig, setMatchMappingConfig] = useState<TournamentStandingsConfig | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [matchDetails, setMatchDetails] = useState<{[key: string]: any}>({});
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>('all');
  const hasInitialized = useRef(false);
  
  // State for knockout inputs
  const [knockoutInputs, setKnockoutInputs] = useState<{[matchId: string]: {level: number, position: number}}>({});
  const [teamStatusInputs, setTeamStatusInputs] = useState<{[configId: string]: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}}}>({});
  
  // Publish functionality - individual loading states for each config
  const [publishLoadingStates, setPublishLoadingStates] = useState<{[configId: string]: boolean}>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Toast notification state
  const [toast, setToast] = useState<{isVisible: boolean, type: 'success' | 'error' | 'info', message: string}>({
    isVisible: false,
    type: 'success',
    message: ''
  });
  
  const handlePublish = async (config: TournamentStandingsConfig) => {
    // Set loading state for this specific config
    setPublishLoadingStates(prev => ({ ...prev, [config.id]: true }));
    
    try {
      console.log('🚀 Starting publish process for config:', config.id);
      
      // Get mapped match IDs from the config
      let mappedMatchIds: string[] = [];
      if (config.match_mapping) {
        if (typeof config.match_mapping === 'string') {
          try {
            mappedMatchIds = JSON.parse(config.match_mapping);
          } catch (e) {
            console.warn('Failed to parse match_mapping:', config.match_mapping);
            mappedMatchIds = [];
          }
        } else if (Array.isArray(config.match_mapping)) {
          mappedMatchIds = config.match_mapping;
        }
      }
      
      console.log('🔍 Mapped match IDs for config:', config.id, ':', mappedMatchIds);
      
      if (mappedMatchIds.length === 0) {
        throw new Error('No matches mapped to this configuration');
      }
      
      // Get calculated stats from standings (match_points data) - ONLY for mapped matches
      const { data: standingsData, error: standingsError } = await supabase
        .from('match_points')
        .select(`
          team_id,
          result,
          goals_for,
          goals_against,
          teams(name),
          matches!inner(tournament_id, season_id)
        `)
        .in('match_id', mappedMatchIds)
        .eq('matches.tournament_id', tournamentId)
        .eq('matches.season_id', config.season_id || null);
      
      if (standingsError) {
        throw new Error(`Failed to fetch standings data: ${standingsError.message}`);
      }
      
      if (!standingsData || standingsData.length === 0) {
        throw new Error('No calculated standings data available');
      }
      
      console.log('📊 Calculated standings data:', standingsData);
      
      // Debug: Log each match point record
      standingsData.forEach((mp, index) => {
        console.log(`🔍 Match Point ${index + 1}:`, {
          team_id: mp.team_id,
          team_name: (mp.teams as any)?.name,
          result: mp.result,
          goals_for: mp.goals_for,
          goals_against: mp.goals_against
        });
      });
      
      // Group by team and calculate aggregates
      const teamAggregates = new Map();
      
      standingsData.forEach(mp => {
        const teamId = mp.team_id;
        if (!teamAggregates.has(teamId)) {
          teamAggregates.set(teamId, {
            team_id: teamId,
            team_name: (mp.teams as any)?.name || 'Unknown Team',
            matches_played: 0,
            wins: 0,
            draws: 0,
            losses: 0,
            goals_for: 0,
            goals_against: 0,
            goal_difference: 0,
            total_points: 0
          });
        }
        
        const team = teamAggregates.get(teamId);
        team.matches_played++;
        
        if (mp.result === 'win') {
          team.wins++;
          team.total_points += config.default_win_points || 0;
        } else if (mp.result === 'draw') {
          team.draws++;
          team.total_points += config.default_draw_points || 0;
        } else if (mp.result === 'loss') {
          team.losses++;
          team.total_points += config.default_loss_points || 0;
        }
        
        team.goals_for += mp.goals_for || 0;
        team.goals_against += mp.goals_against || 0;
      });
      
      // Calculate goal difference and position
      const standings = Array.from(teamAggregates.values()).map(team => {
        team.goal_difference = team.goals_for - team.goals_against;
        return team;
      });
      
      // Sort by points, then goal difference
      standings.sort((a, b) => {
        if (b.total_points !== a.total_points) {
          return b.total_points - a.total_points;
        }
        return b.goal_difference - a.goal_difference;
      });
      
      // Assign positions
      standings.forEach((team, index) => {
        team.position = index + 1;
      });
      
      console.log('📊 Processed standings:', standings);
      
      // Process each team
      let savedCount = 0;
      
      for (const team of standings) {
        // Get checkbox states from UI
        const checkboxStates = teamStatusInputs[config.id]?.[team.team_id] || {
          qualified: false,
          winner: false,
          knockedOut: false
        };
        
        console.log(`📝 Processing team ${team.team_name}:`, {
          calculatedStats: {
            matches_played: team.matches_played,
            wins: team.wins,
            draws: team.draws,
            losses: team.losses,
            goals_for: team.goals_for,
            goals_against: team.goals_against,
            goal_difference: team.goal_difference,
            total_points: team.total_points,
            position: team.position
          },
          checkboxStates: checkboxStates
        });
        
        // Create/update team standings record
        const teamStandingData = {
          tournament_config_id: config.id,
          team_id: team.team_id,
          matches_played: team.matches_played,
          wins: team.wins,
          draws: team.draws,
          losses: team.losses,
          goals_for: team.goals_for,
          goals_against: team.goals_against,
          goal_difference: team.goal_difference,
          total_points: team.total_points,
          position: team.position,
          is_qualified: checkboxStates.qualified,
          is_winner: checkboxStates.winner,
          is_knocked_out: checkboxStates.knockedOut,
          is_active: true
        };
        
        // Check if record exists
        const { data: existingRecord } = await supabase
          .from('team_standings')
          .select('id')
          .eq('tournament_config_id', config.id)
          .eq('team_id', team.team_id)
          .eq('is_active', true)
          .single();
        
        if (existingRecord) {
          // Update existing record
          const { error: updateError } = await supabase
            .from('team_standings')
            .update(teamStandingData)
            .eq('id', existingRecord.id);
          
          if (updateError) {
            console.error(`❌ Error updating team ${team.team_name}:`, updateError);
            throw new Error(`Failed to update team ${team.team_name}: ${updateError.message}`);
          }
        } else {
          // Insert new record
          const { error: insertError } = await supabase
            .from('team_standings')
            .insert(teamStandingData);
          
          if (insertError) {
            console.error(`❌ Error inserting team ${team.team_name}:`, insertError);
            throw new Error(`Failed to insert team ${team.team_name}: ${insertError.message}`);
          }
        }
        
        savedCount++;
        console.log(`✅ Successfully saved team ${team.team_name}`);
      }
      
      // Update knockout matches table if this is a knockout format
      if (config.format_type === 'knockout') {
        console.log('🥊 Updating knockout matches for config:', config.id);
        
        // Process each mapped match
        for (const matchId of mappedMatchIds) {
          const knockoutData = knockoutInputs[matchId];
          
          if (knockoutData && knockoutData.level && knockoutData.position) {
            console.log(`🥊 Processing match ${matchId}:`, knockoutData);
            
            // Check if knockout match record exists
            const { data: existingKnockoutRecord } = await supabase
              .from('knockout_matches')
              .select('id')
              .eq('tournament_config_id', config.id)
              .eq('match_id', matchId)
              .eq('is_active', true)
              .single();
            
            const knockoutMatchData = {
              tournament_config_id: config.id,
              match_id: matchId,
              knockout_level: knockoutData.level,
              level_position: knockoutData.position,
              is_active: true
            };
            
            if (existingKnockoutRecord) {
              // Update existing knockout match record
              const { error: updateKnockoutError } = await supabase
                .from('knockout_matches')
                .update(knockoutMatchData)
                .eq('id', existingKnockoutRecord.id);
              
              if (updateKnockoutError) {
                console.error(`❌ Error updating knockout match ${matchId}:`, updateKnockoutError);
                throw new Error(`Failed to update knockout match ${matchId}: ${updateKnockoutError.message}`);
              }
              
              console.log(`✅ Successfully updated knockout match ${matchId}`);
            } else {
              // Insert new knockout match record
              const { error: insertKnockoutError } = await supabase
                .from('knockout_matches')
                .insert(knockoutMatchData);
              
              if (insertKnockoutError) {
                console.error(`❌ Error inserting knockout match ${matchId}:`, insertKnockoutError);
                throw new Error(`Failed to insert knockout match ${matchId}: ${insertKnockoutError.message}`);
              }
              
              console.log(`✅ Successfully inserted knockout match ${matchId}`);
            }
          } else {
            console.warn(`⚠️ No knockout data found for match ${matchId}`);
          }
        }
      }
      
      // Show success message
      setToast({
        isVisible: true,
        type: 'success',
        message: `Configuration "${config.stage_name || 'General'}" published successfully! ${savedCount} team records saved.`
      });
      
      // Trigger a re-render by updating a state that will cause components to re-fetch data
      // This will update the sync status without a full page refresh
      setPublishLoadingStates(prev => ({ ...prev, [config.id]: false }));
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error: any) {
      console.error('❌ Publish error:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: `Failed to publish configuration: ${error.message}`
      });
    } finally {
      // Clear loading state
      setPublishLoadingStates(prev => ({ ...prev, [config.id]: false }));
    }
  };

  // Extract organization ID from URL
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);

  // Hooks for config functionality
  const { configs, loading: configsLoading, error: configsError, refetch: refetchConfigs } = useAllStandingsConfigs(tournamentId || '');
  const { seasons } = useSeasons();
  const { tournaments } = useTournaments();
  const { updateTieBreakerOrder, updateMatchMapping } = useMatchPointsOperations();


  // Auto-expand first active configuration by default (only on initial load)
  useEffect(() => {
    if (configs.length > 0 && !hasInitialized.current) {
      // Find the first active configuration
      const firstActiveConfig = configs.find(config => config.is_active);
      if (firstActiveConfig) {
        setExpandedRows(new Set([firstActiveConfig.id]));
      } else {
        // If no active configs, expand the first one
        setExpandedRows(new Set([configs[0].id]));
      }
      hasInitialized.current = true;
    }
  }, [configs]);

  // Fetch match details when rows are expanded
  useEffect(() => {
    const fetchAllMatchDetails = async () => {
      for (const config of configs) {
        if (expandedRows.has(config.id)) {
          const matchMapping = typeof config.match_mapping === 'string' 
            ? JSON.parse(config.match_mapping) 
            : config.match_mapping;
          
          if (matchMapping && matchMapping.length > 0) {
            for (const matchId of matchMapping) {
              if (!matchDetails[matchId]) {
                await fetchMatchDetails(matchId);
              }
            }
          }
        }
      }
    };

    if (configs.length > 0 && expandedRows.size > 0) {
      fetchAllMatchDetails();
    }
  }, [configs, expandedRows, matchDetails]);

  // Handle season selection change
  const handleSeasonChange = (value: string) => {
    if (value && value !== '') {
      setSelectedSeasonId(value);
    } else {
      setSelectedSeasonId('all'); // Fallback to "All Seasons"
    }
  };

  // Determine initial tab based on URL hash
  useEffect(() => {
    const hash = location.hash;
    if (hash === '#config') {
      setActiveTab('config');
    } else if (hash === '#points' || hash === '#standings') {
      setActiveTab('points');
    } else {
      // Default to config if no hash or invalid hash
      setActiveTab('config');
    }
  }, [location.hash]);

  // Handle browser back/forward navigation with hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#config') {
        setActiveTab('config');
      } else if (hash === '#points') {
        setActiveTab('points');
      } else if (hash === '#standings') {
        setActiveTab('standings');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Handle go back navigation
  const handleGoBack = () => {
    const actualOrgId = orgIdFromPath || 'default';
    navigate(`/o/${actualOrgId}/score/tournaments`);
  };

  // Handle tab change with hash navigation
  const handleTabChange = (tabId: 'config' | 'points' | 'standings') => {
    setActiveTab(tabId);
    // Update URL hash without causing a page reload
    window.history.replaceState(null, '', `${location.pathname}#${tabId}`);
  };

  const tabs = [
    { id: 'config', label: 'Config', icon: '⚙️' },
    { id: 'points', label: 'Points', icon: '📊' },
    { id: 'standings', label: 'Standings', icon: '🏆' }
  ] as const;

  // Helper function to get season name by ID
  const getSeasonName = (seasonId: string | undefined): string => {
    if (!seasonId) return 'No Season';
    const season = seasons.find(s => s.id === seasonId);
    return season ? season.name : `Season ID: ${seasonId}`;
  };

  // Get active configs filtered by selected season
  const getActiveConfigs = () => {
    const activeConfigs = configs.filter(config => config.is_active);
    
    if (selectedSeasonId === 'all') {
      return activeConfigs;
    }
    
    return activeConfigs.filter(config => config.season_id === selectedSeasonId);
  };

  // Get unique seasons from active configs
  const getAvailableSeasons = () => {
    const activeConfigs = configs.filter(config => config.is_active);
    const seasonIds = [...new Set(activeConfigs.map(config => config.season_id).filter(Boolean))];
    
    return [
      { id: 'all', name: 'All Seasons' },
      ...seasonIds.map(seasonId => ({
        id: seasonId!,
        name: getSeasonName(seasonId!)
      }))
    ];
  };


  // Handle tie-breaker order update
  const handleTieBreakerOrderUpdate = async (tieBreakerOrder: string[]) => {
    if (!tieBreakerConfig) return;

    try {
      await updateTieBreakerOrder(tieBreakerConfig.id, tieBreakerOrder);
      await refetchConfigs();
      console.log('Tie-breaker order updated successfully:', tieBreakerOrder);
    } catch (error) {
      console.error('Failed to update tie-breaker order:', error);
    }
  };

  const handleMatchMappingUpdate = async (matchIds: string[]) => {
    if (!matchMappingConfig) return;

    try {
      await updateMatchMapping(matchMappingConfig.id, matchIds);
      await refetchConfigs();
      console.log('Match mapping updated successfully:', matchIds);
    } catch (error) {
      console.error('Failed to update match mapping:', error);
    }
  };

  const toggleRowExpansion = (configId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(configId)) {
        newSet.delete(configId);
      } else {
        newSet.add(configId);
      }
      return newSet;
    });
  };

  // Fetch match details for a given match ID
  const fetchMatchDetails = async (matchId: string) => {
    if (matchDetails[matchId]) {
      return matchDetails[matchId]; // Return cached data
    }
    
    try {
      const details = await matchDetailsService.getMatchDetails(matchId);
      if (details) {
        setMatchDetails(prev => ({
          ...prev,
          [matchId]: details
        }));
        return details;
      }
    } catch (error) {
      console.error('Error fetching match details:', error);
    }
    return null;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'config':
        if (configsLoading) {
          return (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-4">⏳</div>
                <p className="text-gray-500">Loading configurations...</p>
              </div>
            </div>
          );
        }

        if (configsError) {
          return (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="text-red-400 text-4xl mb-4">❌</div>
                <p className="text-red-500">Error loading configurations: {configsError}</p>
              </div>
            </div>
          );
        }

        if (!configs || configs.length === 0) {
          return (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">⚙️</div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Configurations Found</h2>
                <p className="text-gray-500">Tournament configurations will be displayed here.</p>
                <p className="text-sm text-gray-400 mt-2">Tournament ID: {tournamentId}</p>
              </div>
            </div>
          );
        }

        return (
          <div className="p-6">
            <div className="max-w-6xl mx-auto">
              {/* Table Container */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Header Row */}
                <div className="bg-gray-50 border-b border-gray-200">
                  <div className="grid grid-cols-12 gap-4 p-4 text-sm font-medium text-gray-600">
                    <div className="col-span-4">Season</div>
                    <div className="col-span-4">Stage Name</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Actions</div>
                  </div>
                </div>
                
                {/* Configuration Rows */}
                <div className="divide-y divide-gray-200">
                  {configs.map((config) => (
                    <div key={config.id} className="hover:bg-gray-50 transition-colors duration-200">
                        <div className="p-4">
                          <div className="grid grid-cols-12 gap-4 items-center">
                            {/* Season Column */}
                            <div className="col-span-4">
                              <span className="text-sm font-medium text-gray-900">
                                {getSeasonName(config.season_id)}
                              </span>
                            </div>
                            
                            {/* Stage Name Column */}
                            <div className="col-span-4">
                              <span className="text-sm text-gray-700">
                                {config.stage_name || ''}
                              </span>
                            </div>
                            
                            {/* Status Column */}
                            <div className="col-span-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                config.is_active 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {config.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            
                            {/* Actions Column */}
                            <div className="col-span-2">
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => {
                                    setEditingConfig(config);
                                    setIsConfigModalOpen(true);
                                  }}
                                  className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit Configuration"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => {
                                    setTieBreakerConfig(config);
                                    setIsTieBreakerModalOpen(true);
                                  }}
                                  className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded transition-colors"
                                  title="Set Tie-Breaker Order"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => {
                                    setMatchMappingConfig(config);
                                    setIsMatchMappingModalOpen(true);
                                  }}
                                  className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                                  title="Map Matches"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => toggleRowExpansion(config.id)}
                                  className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                                  title="View Details"
                                >
                                  <svg 
                                    className={`w-4 h-4 transition-transform duration-200 ${expandedRows.has(config.id) ? 'rotate-180' : ''}`} 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Expanded Details Section */}
                        {expandedRows.has(config.id) && (
                          <div className="border-t border-gray-200 bg-gray-50">
                            <div className="p-6">
                              <div className="flex flex-col space-y-6">
                                
                                {/* Tournament Configuration Details */}
                                <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center mr-2">
                                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      </svg>
                                    </div>
                                    Configuration Details
                                  </h3>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="bg-gray-50 rounded p-3">
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Format Type</span>
                                        <span className="text-sm font-medium text-gray-900 bg-gray-200 px-2 py-1 rounded">
                                          {config.format_type || 'Not set'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="bg-gray-50 rounded p-3">
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Created</span>
                                        <span className="text-sm font-medium text-gray-900">
                                          {new Date(config.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="bg-gray-50 rounded p-3">
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Points for Win</span>
                                        <span className="text-sm font-medium text-gray-900 bg-gray-200 px-2 py-1 rounded">
                                          {config.default_win_points ?? 'Not set'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="bg-gray-50 rounded p-3">
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Points for Draw</span>
                                        <span className="text-sm font-medium text-gray-900 bg-gray-200 px-2 py-1 rounded">
                                          {config.default_draw_points ?? 'Not set'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="bg-gray-50 rounded p-3">
                                      <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Points for Loss</span>
                                        <span className="text-sm font-medium text-gray-900 bg-gray-200 px-2 py-1 rounded">
                                          {config.default_loss_points ?? 'Not set'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Tie-Breaker Order Details */}
                                <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <div className="w-6 h-6 bg-orange-100 rounded flex items-center justify-center mr-2">
                                      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                      </svg>
                                    </div>
                                    Tie-Breaker Order
                                  </h3>
                                  <div className="space-y-3">
                                    {config.tie_breaker_order && config.tie_breaker_order.length > 0 ? (
                                      <div className="space-y-2">
                                        {config.tie_breaker_order.map((criteria, index) => (
                                          <div key={index} className="flex items-center space-x-3 bg-gray-50 rounded p-3">
                                            <span className="w-6 h-6 bg-gray-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                                              {index + 1}
                                            </span>
                                            <span className="text-sm font-medium text-gray-900 capitalize">
                                              {criteria.replace(/_/g, ' ')}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-center py-6">
                                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                          </svg>
                                        </div>
                                        <p className="text-sm text-gray-500 font-medium">No tie-breaker order set</p>
                                        <p className="text-xs text-gray-400 mt-1">Configure tie-breaker criteria to determine team rankings</p>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Match Mapping Details */}
                                <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <div className="w-6 h-6 bg-green-100 rounded flex items-center justify-center mr-2">
                                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                      </svg>
                                    </div>
                                    Match Mapping
                                  </h3>
                                  <div className="space-y-3">
                                    {(() => {
                                      const matchMapping = typeof config.match_mapping === 'string' 
                                        ? JSON.parse(config.match_mapping) 
                                        : config.match_mapping;
                                      return matchMapping && matchMapping.length > 0 ? (
                                        <div className="space-y-3">
                                          <div className="bg-gray-50 rounded p-3">
                                            <div className="flex items-center space-x-2">
                                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                              </svg>
                                              <span className="text-sm font-medium text-gray-700">
                                                {matchMapping.length} match{matchMapping.length !== 1 ? 'es' : ''} mapped
                                              </span>
                                            </div>
                                          </div>
                                          <div className="max-h-48 overflow-y-auto space-y-2">
                                            {matchMapping.map((matchId: string, index: number) => {
                                              const matchDetail = matchDetails[matchId];
                                              return (
                                                <div key={matchId} className="bg-gray-50 rounded p-3 border border-gray-200">
                                                  <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                      <span className="w-5 h-5 bg-gray-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                                                        {index + 1}
                                                      </span>
                                                      <div className="flex-1">
                                                        {matchDetail ? (
                                                          <>
                                                            <div className="flex items-center space-x-2">
                                                              <span className="text-sm text-gray-700">{matchDetail.home_team.name}</span>
                                                              <span className="text-sm font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                                                {matchDetail.home_score} - {matchDetail.away_score}
                                                              </span>
                                                              <span className="text-sm text-gray-700">{matchDetail.away_team.name}</span>
                                                            </div>
                                                            {matchDetail.home_penalty_score !== undefined && matchDetail.away_penalty_score !== undefined &&
                                                             (matchDetail.home_penalty_score > 0 || matchDetail.away_penalty_score > 0) &&
                                                              <p className="text-xs text-gray-500 mt-1">penalties ({matchDetail.home_penalty_score}) - ({matchDetail.away_penalty_score})</p>
                                                            }
                                                          </>
                                                        ) : (
                                                          <>
                                                            <p className="text-sm font-medium text-gray-900">
                                                              Match {index + 1}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                              {matchId.substring(0, 8)}...
                                                            </p>
                                                          </>
                                                        )}
                                                      </div>
                                                    </div>
                                                    <div className="text-right">
                                                      <p className="text-xs text-gray-500">
                                                        {matchDetail ? new Date(matchDetail.start_date).toLocaleDateString() : new Date().toLocaleDateString()}
                                                      </p>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="text-center py-6">
                                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                            </svg>
                                          </div>
                                          <p className="text-sm text-gray-500 font-medium">No matches mapped</p>
                                          <p className="text-xs text-gray-400 mt-1">Map matches to include them in standings calculations</p>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                  </div>
                ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'points':
        const activeConfigs = getActiveConfigs();
        
        if (configsLoading) {
          return (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-4">⏳</div>
                <p className="text-gray-500">Loading standings...</p>
              </div>
            </div>
          );
        }

        if (configsError) {
          return (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="text-red-400 text-4xl mb-4">❌</div>
                <p className="text-red-500">Error loading standings: {configsError}</p>
              </div>
            </div>
          );
        }

        if (activeConfigs.length === 0) {
          return (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📊</div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Active Configurations</h2>
                <p className="text-gray-500">No active tournament configurations found for standings.</p>
                <p className="text-sm text-gray-400 mt-2">Activate a configuration in the Config tab to view standings.</p>
              </div>
            </div>
          );
        }

        return (
          <div className="p-6">
            <div className="max-w-6xl mx-auto">

            {/* Standings Content */}
            <div className="space-y-6">
              {activeConfigs.map((config) => {
                return (
                  <div key={config.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">
                            {config.stage_name || 'General Configuration'}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {getSeasonName(config.season_id)} • {config.format_type || 'Unknown Format'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => handlePublish(config)}
                            disabled={publishLoadingStates[config.id] || false}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Publish configuration"
                          >
                            {publishLoadingStates[config.id] ? 'Publishing...' : 'Publish'}
                          </button>
                        </div>
                      </div>

                    {/* Format-based rendering */}
                    {config.format_type === 'league' && tournamentId && (
                      <LeagueStandingsTable 
                        tournamentId={tournamentId} 
                        seasonId={config.season_id}
                        config={config}
                        teamStatusInputs={teamStatusInputs}
                        setTeamStatusInputs={setTeamStatusInputs}
                        refreshTrigger={refreshTrigger}
                      />
                    )}

                      {config.format_type === 'knockout' && tournamentId && (
                        <KnockoutMatchesTable 
                          tournamentId={tournamentId} 
                          seasonId={config.season_id}
                          config={config}
                          knockoutInputs={knockoutInputs}
                          setKnockoutInputs={setKnockoutInputs}
                          teamStatusInputs={teamStatusInputs}
                          setTeamStatusInputs={setTeamStatusInputs}
                          refreshTrigger={refreshTrigger}
                        />
                      )}

                    {(config.format_type === 'group_stage' || config.format_type === 'group') && tournamentId && (
                      <GroupStandingsTable 
                        tournamentId={tournamentId} 
                        seasonId={config.season_id}
                        config={config}
                        teamStatusInputs={teamStatusInputs}
                        setTeamStatusInputs={setTeamStatusInputs}
                        refreshTrigger={refreshTrigger}
                      />
                    )}

                      {!config.format_type && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h5 className="text-md font-semibold text-gray-900 mb-3">Standings</h5>
                          <div className="text-center py-8">
                            <div className="text-gray-400 text-4xl mb-3">📊</div>
                            <p className="text-gray-600">Standings will be displayed here</p>
                            <p className="text-sm text-gray-500 mt-1">
                              Format not specified in configuration
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        );
      case 'standings':
        const standingsActiveConfigs = getActiveConfigs();
        return (
          <div className="p-6">
            <div className="max-w-6xl mx-auto">
              {/* Active Configurations */}
              {standingsActiveConfigs.length > 0 ? (
                <div className="space-y-6">
                  {standingsActiveConfigs.map((config) => {
                    const rules = generateTieBreakerRules(config);
                    
                    return (
                      <div key={config.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {/* Config Header */}
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-md font-semibold text-gray-900">
                                {config.stage_name || 'General Configuration'}
                              </h5>
                              <p className="text-sm text-gray-600 mt-1">
                                {config.season_id ? seasons.find(s => s.id === config.season_id)?.name : 'All Seasons'} • {config.format_type}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">{rules.pointsText}</p>
                              <p className="text-xs text-gray-500">{rules.tieBreakerText}</p>
                            </div>
                          </div>
                        </div>

                        {/* Format-based rendering */}
                        {config.format_type === 'league' && tournamentId && (
                          <LeagueStandingsDisplay 
                            tournamentId={tournamentId} 
                            seasonId={selectedSeasonId === 'all' ? config.season_id : selectedSeasonId}
                            config={config}
                          />
                        )}

                        {config.format_type === 'knockout' && tournamentId && (
                          <KnockoutStandingsDisplay 
                            tournamentId={tournamentId} 
                            seasonId={selectedSeasonId === 'all' ? config.season_id : selectedSeasonId}
                            config={config}
                          />
                        )}

                        {(config.format_type === 'group_stage' || config.format_type === 'group') && tournamentId && (
                          <GroupStandingsDisplay 
                            tournamentId={tournamentId} 
                            seasonId={selectedSeasonId === 'all' ? config.season_id : selectedSeasonId}
                            config={config}
                          />
                        )}

                        {!config.format_type && (
                          <div className="p-6">
                            <div className="text-center py-8">
                              <div className="text-gray-400 text-4xl mb-3">📊</div>
                              <p className="text-gray-600">Standings will be displayed here</p>
                              <p className="text-sm text-gray-500 mt-1">
                                Format not specified in configuration
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">🏆</div>
                  <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Active Configurations</h2>
                  <p className="text-gray-500">Create a tournament configuration to view standings.</p>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Go Back, Tournament Card, and Season Dropdown */}
      <ScoreAppHeader
        leftContent={
          <Button
            onClick={handleGoBack}
            variant="ghost"
            size="md"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            }
            iconPosition="left"
          >
            Go Back
          </Button>
        }
        centerContent={
          tournamentId && tournaments.length > 0 ? (
            <div className="flex items-center justify-center flex-1">
              {(() => {
                const tournament = tournaments.find(t => t.id === tournamentId);
                if (!tournament) return null;
                
                return (
                  <div className="flex items-center space-x-3">
                    {/* Tournament Logo */}
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                      {tournament.logo ? (
                        <img 
                          src={tournament.logo} 
                          alt={tournament.name}
                          className="w-8 h-8 object-contain"
                        />
                      ) : (
                        <span className="text-blue-600 text-lg font-bold">
                          {tournament.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    
                    <div>
                      <h1 className="text-xl font-semibold text-gray-900">{tournament.name}</h1>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span>• {tournament.country || 'International'}</span>
                        <span>Tournament Details</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : null
        }
        rightContent={
          activeTab === 'config' ? (
            <Button
              onClick={() => {
                setEditingConfig(null);
                setIsConfigModalOpen(true);
              }}
              variant="primary"
              size="md"
            >
              + Config
            </Button>
          ) : (activeTab === 'points' || activeTab === 'standings') ? (
            <SearchableDropdown
              value={selectedSeasonId}
              onChange={handleSeasonChange}
              options={getAvailableSeasons()}
              placeholder="Select season"
              className="w-48"
            />
          ) : null
        }
      />

      {/* Tabs Section */}
      <div className="px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        {/* Tab Content */}
        {renderTabContent()}
      </div>

          {/* Configuration Modal */}
          <TournamentConfigModal
            isOpen={isConfigModalOpen}
            onClose={() => {
              setIsConfigModalOpen(false);
              setEditingConfig(null);
            }}
            onSuccess={() => {
              refetchConfigs();
              setIsConfigModalOpen(false);
              setEditingConfig(null);
            }}
            tournamentId={tournamentId || ''}
            initialConfig={editingConfig}
          />

          {/* Tie-Breaker Order Modal */}
          <TieBreakerOrderModal
            isOpen={isTieBreakerModalOpen}
            onClose={() => {
              setIsTieBreakerModalOpen(false);
              setTieBreakerConfig(null);
            }}
            onSuccess={(tieBreakerOrder) => {
              handleTieBreakerOrderUpdate(tieBreakerOrder);
              setIsTieBreakerModalOpen(false);
              setTieBreakerConfig(null);
            }}
            initialOrder={tieBreakerConfig?.tie_breaker_order || []}
          />

          <MatchMappingModal
            isOpen={isMatchMappingModalOpen}
            onClose={() => {
              setIsMatchMappingModalOpen(false);
              setMatchMappingConfig(null);
            }}
            onSuccess={(matchIds) => {
              handleMatchMappingUpdate(matchIds);
              setIsMatchMappingModalOpen(false);
              setMatchMappingConfig(null);
            }}
            tournamentId={tournamentId || ''}
            seasonId={matchMappingConfig?.season_id}
            initialMatchIds={matchMappingConfig?.match_mapping ? 
              (typeof matchMappingConfig.match_mapping === 'string' 
                ? JSON.parse(matchMappingConfig.match_mapping) 
                : matchMappingConfig.match_mapping) 
              : []}
          />
          
          {/* Toast Notification */}
          <Toast
            isVisible={toast.isVisible}
            onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
            type={toast.type}
            message={toast.message}
            duration={6000}
          />
        </div>
      );
    };

    // League Standings Table Component
interface LeagueStandingsTableProps {
  tournamentId: string;
  seasonId?: string;
  config: TournamentStandingsConfig;
  teamStatusInputs: {[configId: string]: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}}};
  setTeamStatusInputs: React.Dispatch<React.SetStateAction<{[configId: string]: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}}}>>;
  refreshTrigger: number;
}

const LeagueStandingsTable: React.FC<LeagueStandingsTableProps> = ({ 
  tournamentId, 
  seasonId, 
  config,
  teamStatusInputs,
  setTeamStatusInputs,
  refreshTrigger
}) => {
  const { standings, loading, error } = useTournamentStandings(tournamentId, seasonId);
  
  // Fetch team standings data for checkbox states
  const [teamStandingsData, setTeamStandingsData] = useState<any[]>([]);
  const [teamStandingsLoading, setTeamStandingsLoading] = useState(true);
  
  useEffect(() => {
    const fetchTeamStandings = async () => {
      if (!config.id) return;
      
      try {
        setTeamStandingsLoading(true);
        const { data, error } = await supabase
          .from('team_standings')
          .select(`
            *,
            teams!inner(name)
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true);
        
        if (error) {
          console.error('Error fetching team standings:', error);
          return;
        }
        
        console.log('🔍 Team Standings Debug - Config ID:', config.id);
        console.log('🔍 Team Standings Data:', data);
        console.log('🔍 Team Standings Count:', data?.length);
        
        // Check for duplicate teams in the same config
        if (data && data.length > 0) {
          const teamIds = data.map(team => team.team_id);
          const uniqueTeamIds = [...new Set(teamIds)];
          console.log('🔍 Unique Team IDs:', uniqueTeamIds);
          console.log('🔍 Total Teams:', teamIds.length, 'Unique Teams:', uniqueTeamIds.length);
          
          if (teamIds.length !== uniqueTeamIds.length) {
            console.warn('⚠️ Duplicate teams found in team_standings for config:', config.id);
            data.forEach(team => {
              console.log(`Team ${team.team_id}: P=${team.matches_played}, Pts=${team.total_points}`);
            });
          }
        }
        
        // Handle duplicate teams by keeping only the most recent record
        let processedData = data || [];
        if (processedData.length > 0) {
          const teamMap = new Map();
          processedData.forEach(team => {
            const existingTeam = teamMap.get(team.team_id);
            if (!existingTeam || new Date(team.created_at || team.updated_at) > new Date(existingTeam.created_at || existingTeam.updated_at)) {
              teamMap.set(team.team_id, team);
            }
          });
          processedData = Array.from(teamMap.values());
          console.log('🔍 Processed Team Standings (deduplicated):', processedData.length, 'teams');
        }
        
        setTeamStandingsData(processedData);
        
        // Initialize teamStatusInputs from database
        if (data && data.length > 0) {
          const initialStatusInputs: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}} = {};
          data.forEach((team: any) => {
            initialStatusInputs[team.team_id] = {
              qualified: team.is_qualified || false,
              winner: team.is_winner || false,
              knockedOut: team.is_knocked_out || false
            };
          });
          setTeamStatusInputs(prev => ({
            ...prev,
            [config.id]: initialStatusInputs
          }));
        }
      } catch (err) {
        console.error('Error fetching team standings:', err);
      } finally {
        setTeamStandingsLoading(false);
      }
    };
    
    fetchTeamStandings();
  }, [config.id, setTeamStatusInputs, refreshTrigger]);

  // Simple sync check - always show publish required if there are any UI changes
  const isDataSynced = () => {
    // If no team standings data exists, we need to publish
    if (!teamStandingsData || teamStandingsData.length === 0) {
      return false;
    }
    
    // Check if any checkbox states differ from database
    if (teamStatusInputs[config.id]) {
      for (const [teamId, inputs] of Object.entries(teamStatusInputs[config.id])) {
        const dbRecord = teamStandingsData.find(ts => ts.team_id === teamId);
        if (dbRecord) {
          if (
            dbRecord.is_qualified !== inputs.qualified ||
            dbRecord.is_winner !== inputs.winner ||
            dbRecord.is_knocked_out !== inputs.knockedOut
          ) {
            return false; // Found a difference, need to publish
          }
        }
      }
    }
    
    return true; // All data matches
  };

  if (loading || teamStandingsLoading) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-md font-semibold text-gray-900 mb-3">League Standings</h5>
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading standings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-md font-semibold text-gray-900 mb-3">League Standings</h5>
        <div className="text-center py-8">
          <div className="text-red-400 text-4xl mb-3">❌</div>
          <p className="text-red-600">Error loading standings</p>
          <p className="text-sm text-red-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!standings || standings.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-md font-semibold text-gray-900 mb-3">League Standings</h5>
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">🏆</div>
          <p className="text-gray-600">No standings data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Points: Win ({config.default_win_points || 0}), Draw ({config.default_draw_points || 0}), Loss ({config.default_loss_points || 0})
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-md font-semibold text-gray-900">League Standings</h5>
        
        {/* Sync Status Message */}
        <div className="flex items-center space-x-2">
          {isDataSynced() ? (
            <div className="flex items-center space-x-1 text-green-600 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">All data synced</span>
            </div>
          ) : (
            <div className="flex items-center space-x-1 text-orange-600 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="font-medium">Publish required</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Standings Table */}
      <div className="space-y-6">
        {/* Table 1: Calculated Stats from match_points (No Checkboxes) */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h6 className="text-sm font-semibold text-gray-900">Calculated Statistics</h6>
            <p className="text-xs text-gray-600 mt-1">Data calculated from match results</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GD</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {standings.map((team, index) => (
                  <tr key={team.team_id} className="hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                        team.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                        team.position === 2 ? 'bg-gray-100 text-gray-800' :
                        team.position === 3 ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {team.position}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">{team.team_name}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                    <td className={`py-3 px-3 text-center font-medium ${
                      team.goal_difference > 0 ? 'text-green-600' :
                      team.goal_difference < 0 ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                      {team.goal_difference > 0 ? `+${team.goal_difference}` : team.goal_difference}
                    </td>
                    <td className="py-3 px-4 text-center font-bold text-gray-900">{team.total_points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Table 2: Team Status from team_standings (With Checkboxes) */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h6 className="text-sm font-semibold text-gray-900">Team Status</h6>
            <p className="text-xs text-gray-600 mt-1">Data from team_standings table with status settings</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GD</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teamStandingsData
                  .filter(teamStanding => {
                    // Only show teams that are part of the current tournament configuration
                    return standings.some(s => s.team_id === teamStanding.team_id);
                  })
                  .map((teamStanding) => {
                  // Find the team name from standings data
                  const teamInfo = standings.find(s => s.team_id === teamStanding.team_id);
                  return (
                    <tr key={teamStanding.team_id} className="hover:bg-gray-50">
                      <td className="py-3 px-3">
                        <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                          teamStanding.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                          teamStanding.position === 2 ? 'bg-gray-100 text-gray-800' :
                          teamStanding.position === 3 ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {teamStanding.position}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900">{teamInfo?.team_name || 'Unknown Team'}</td>
                      <td className="py-3 px-3 text-center text-gray-600">{teamStanding.matches_played}</td>
                      <td className="py-3 px-3 text-center text-gray-600">{teamStanding.wins}</td>
                      <td className="py-3 px-3 text-center text-gray-600">{teamStanding.draws}</td>
                      <td className="py-3 px-3 text-center text-gray-600">{teamStanding.losses}</td>
                      <td className="py-3 px-3 text-center text-gray-600">{teamStanding.goals_for}</td>
                      <td className="py-3 px-3 text-center text-gray-600">{teamStanding.goals_against}</td>
                      <td className={`py-3 px-3 text-center font-medium ${
                        teamStanding.goal_difference > 0 ? 'text-green-600' :
                        teamStanding.goal_difference < 0 ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {teamStanding.goal_difference > 0 ? `+${teamStanding.goal_difference}` : teamStanding.goal_difference}
                      </td>
                      <td className="py-3 px-4 text-center font-bold text-gray-900">{teamStanding.total_points}</td>
                      <td className="py-3 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={teamStatusInputs[config.id]?.[teamStanding.team_id]?.qualified || false}
                          onChange={(e) => {
                            setTeamStatusInputs(prev => ({
                              ...prev,
                              [config.id]: {
                                ...prev[config.id],
                                [teamStanding.team_id]: {
                                  ...prev[config.id]?.[teamStanding.team_id],
                                  qualified: e.target.checked,
                                  winner: prev[config.id]?.[teamStanding.team_id]?.winner || false,
                                  knockedOut: prev[config.id]?.[teamStanding.team_id]?.knockedOut || false
                                }
                              }
                            }));
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="py-3 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={teamStatusInputs[config.id]?.[teamStanding.team_id]?.winner || false}
                          onChange={(e) => {
                            setTeamStatusInputs(prev => ({
                              ...prev,
                              [config.id]: {
                                ...prev[config.id],
                                [teamStanding.team_id]: {
                                  ...prev[config.id]?.[teamStanding.team_id],
                                  qualified: prev[config.id]?.[teamStanding.team_id]?.qualified || false,
                                  winner: e.target.checked,
                                  knockedOut: prev[config.id]?.[teamStanding.team_id]?.knockedOut || false
                                }
                              }
                            }));
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="py-3 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={teamStatusInputs[config.id]?.[teamStanding.team_id]?.knockedOut || false}
                          onChange={(e) => {
                            setTeamStatusInputs(prev => ({
                              ...prev,
                              [config.id]: {
                                ...prev[config.id],
                                [teamStanding.team_id]: {
                                  ...prev[config.id]?.[teamStanding.team_id],
                                  qualified: prev[config.id]?.[teamStanding.team_id]?.qualified || false,
                                  winner: prev[config.id]?.[teamStanding.team_id]?.winner || false,
                                  knockedOut: e.target.checked
                                }
                              }
                            }));
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Config Rules */}
      <div className="mt-4 bg-blue-50 rounded-lg p-3 border border-blue-200">
        <div className="flex items-center space-x-2 mb-2">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h6 className="text-sm font-semibold text-blue-900">Config Rules</h6>
        </div>
        <div className="text-sm text-blue-800 space-y-1">
          {(() => {
            const rules = generateTieBreakerRules(config);
            return (
              <>
                <p>• {rules.pointsText}</p>
                <p>• {rules.tieBreakerText}</p>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

// Group Standings Table Component
interface GroupStandingsTableProps {
  tournamentId: string;
  seasonId?: string;
  config: TournamentStandingsConfig;
  teamStatusInputs: {[configId: string]: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}}};
  setTeamStatusInputs: React.Dispatch<React.SetStateAction<{[configId: string]: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}}}>>;
  refreshTrigger: number;
}

const GroupStandingsTable: React.FC<GroupStandingsTableProps> = ({ 
  tournamentId, 
  seasonId, 
  config,
  teamStatusInputs,
  setTeamStatusInputs,
  refreshTrigger
}) => {
  const { groupStandings, loading, error } = useGroupStandings(tournamentId, seasonId);
  
  // Debug logging for group standings
  console.log('🔍 Group Standings Debug - tournamentId:', tournamentId, 'seasonId:', seasonId);
  console.log('🔍 Group Standings Data:', groupStandings);
  
  // Fetch team standings data for checkbox states
  const [teamStandingsData, setTeamStandingsData] = useState<any[]>([]);
  const [teamStandingsLoading, setTeamStandingsLoading] = useState(true);
  
  useEffect(() => {
    const fetchTeamStandings = async () => {
      if (!config.id) return;
      
      try {
        setTeamStandingsLoading(true);
        const { data, error } = await supabase
          .from('team_standings')
          .select(`
            *,
            teams!inner(name)
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true);
        
        if (error) {
          console.error('Error fetching team standings:', error);
          return;
        }
        
        console.log('🔍 Team Standings Debug - Config ID:', config.id);
        console.log('🔍 Team Standings Data:', data);
        console.log('🔍 Team Standings Count:', data?.length);
        
        // Check for duplicate teams in the same config
        if (data && data.length > 0) {
          const teamIds = data.map(team => team.team_id);
          const uniqueTeamIds = [...new Set(teamIds)];
          console.log('🔍 Unique Team IDs:', uniqueTeamIds);
          console.log('🔍 Total Teams:', teamIds.length, 'Unique Teams:', uniqueTeamIds.length);
          
          if (teamIds.length !== uniqueTeamIds.length) {
            console.warn('⚠️ Duplicate teams found in team_standings for config:', config.id);
            data.forEach(team => {
              console.log(`Team ${team.team_id}: P=${team.matches_played}, Pts=${team.total_points}`);
            });
          }
        }
        
        // Handle duplicate teams by keeping only the most recent record
        let processedData = data || [];
        if (processedData.length > 0) {
          const teamMap = new Map();
          processedData.forEach(team => {
            const existingTeam = teamMap.get(team.team_id);
            if (!existingTeam || new Date(team.created_at || team.updated_at) > new Date(existingTeam.created_at || existingTeam.updated_at)) {
              teamMap.set(team.team_id, team);
            }
          });
          processedData = Array.from(teamMap.values());
          console.log('🔍 Processed Team Standings (deduplicated):', processedData.length, 'teams');
        }
        
        setTeamStandingsData(processedData);
        
        // Initialize teamStatusInputs from database
        if (data && data.length > 0) {
          const initialStatusInputs: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}} = {};
          data.forEach((team: any) => {
            initialStatusInputs[team.team_id] = {
              qualified: team.is_qualified || false,
              winner: team.is_winner || false,
              knockedOut: team.is_knocked_out || false
            };
          });
          setTeamStatusInputs(prev => ({
            ...prev,
            [config.id]: initialStatusInputs
          }));
        }
      } catch (err) {
        console.error('Error fetching team standings:', err);
      } finally {
        setTeamStandingsLoading(false);
      }
    };
    
    fetchTeamStandings();
  }, [config.id, setTeamStatusInputs, refreshTrigger]);

  // Simple sync check - always show publish required if there are any UI changes
  const isDataSynced = () => {
    // If no team standings data exists, we need to publish
    if (!teamStandingsData || teamStandingsData.length === 0) {
      return false;
    }
    
    // Check if any checkbox states differ from database
    if (teamStatusInputs[config.id]) {
      for (const [teamId, inputs] of Object.entries(teamStatusInputs[config.id])) {
        const dbRecord = teamStandingsData.find(ts => ts.team_id === teamId);
        if (dbRecord) {
          if (
            dbRecord.is_qualified !== inputs.qualified ||
            dbRecord.is_winner !== inputs.winner ||
            dbRecord.is_knocked_out !== inputs.knockedOut
          ) {
            return false; // Found a difference, need to publish
          }
        }
      }
    }
    
    return true; // All data matches
  };

  console.log('📊 GroupStandingsTable - tournamentId:', tournamentId, 'seasonId:', seasonId);
  console.log('📊 GroupStandingsTable - groupStandings:', groupStandings);
  console.log('📊 GroupStandingsTable - loading:', loading, 'error:', error);

  if (loading || teamStandingsLoading) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-md font-semibold text-gray-900 mb-3">Group Stage</h5>
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading group standings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-md font-semibold text-gray-900 mb-3">Group Stage</h5>
        <div className="text-center py-8">
          <div className="text-red-400 text-4xl mb-3">❌</div>
          <p className="text-red-600">Error loading group standings</p>
          <p className="text-sm text-red-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!groupStandings || groupStandings.size === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-md font-semibold text-gray-900 mb-3">Group Stage</h5>
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">👥</div>
          <p className="text-gray-600">No group standings data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Points: Win ({config.default_win_points || 0}), Draw ({config.default_draw_points || 0}), Loss ({config.default_loss_points || 0})
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-md font-semibold text-gray-900">Group Stage</h5>
        
        {/* Sync Status Message */}
        <div className="flex items-center space-x-2">
          {isDataSynced() ? (
            <div className="flex items-center space-x-1 text-green-600 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">All data synced</span>
            </div>
          ) : (
            <div className="flex items-center space-x-1 text-orange-600 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="font-medium">Publish required</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="space-y-6">
        {Array.from(groupStandings.entries()).map(([groupName, standings]) => (
          <div key={groupName} className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h6 className="text-lg font-semibold text-gray-900">{groupName}</h6>
              <span className="text-sm text-gray-500">{standings.length} teams</span>
            </div>
            
            {/* Table 1: Calculated Statistics from match_points */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h6 className="text-sm font-semibold text-gray-900">Calculated Statistics</h6>
                <p className="text-xs text-gray-600 mt-1">Data calculated from match results</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">GD</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {standings.map((team) => (
                      <tr key={team.team_id} className="hover:bg-gray-50">
                        <td className="py-3 px-3">
                          <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                            team.position === 1 ? 'bg-green-100 text-green-800' :
                            team.position === 2 ? 'bg-blue-100 text-blue-800' :
                            team.position === 3 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {team.position}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900">{team.team_name}</td>
                        <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                        <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                        <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                        <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                        <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                        <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                        <td className={`py-3 px-3 text-center font-medium ${
                          team.goal_difference > 0 ? 'text-green-600' :
                          team.goal_difference < 0 ? 'text-red-600' :
                          'text-gray-600'
                        }`}>
                          {team.goal_difference > 0 ? `+${team.goal_difference}` : team.goal_difference}
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-gray-900">{team.total_points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Table 2: Team Status from team_standings (With Checkboxes) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h6 className="text-sm font-semibold text-gray-900">Team Status</h6>
                <p className="text-xs text-gray-600 mt-1">Data from team_standings table with status settings</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-600">GD</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
                      <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                      <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                      <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {teamStandingsData
                      .filter(teamStanding => {
                        // Only show teams that are part of the current tournament configuration
                        return standings.some(s => s.team_id === teamStanding.team_id);
                      })
                      .map((teamStanding) => {
                      // Find the team name from standings data
                      const teamInfo = standings.find(s => s.team_id === teamStanding.team_id);
                      return (
                        <tr key={teamStanding.team_id} className="hover:bg-gray-50">
                          <td className="py-3 px-3">
                            <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                              teamStanding.position === 1 ? 'bg-green-100 text-green-800' :
                              teamStanding.position === 2 ? 'bg-blue-100 text-blue-800' :
                              teamStanding.position === 3 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {teamStanding.position}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-900">{teamInfo?.team_name || 'Unknown Team'}</td>
                          <td className="py-3 px-3 text-center text-gray-600">{teamStanding.matches_played}</td>
                          <td className="py-3 px-3 text-center text-gray-600">{teamStanding.wins}</td>
                          <td className="py-3 px-3 text-center text-gray-600">{teamStanding.draws}</td>
                          <td className="py-3 px-3 text-center text-gray-600">{teamStanding.losses}</td>
                          <td className="py-3 px-3 text-center text-gray-600">{teamStanding.goals_for}</td>
                          <td className="py-3 px-3 text-center text-gray-600">{teamStanding.goals_against}</td>
                          <td className={`py-3 px-3 text-center font-medium ${
                            teamStanding.goal_difference > 0 ? 'text-green-600' :
                            teamStanding.goal_difference < 0 ? 'text-red-600' :
                            'text-gray-600'
                          }`}>
                            {teamStanding.goal_difference > 0 ? `+${teamStanding.goal_difference}` : teamStanding.goal_difference}
                          </td>
                          <td className="py-3 px-4 text-center font-bold text-gray-900">{teamStanding.total_points}</td>
                          <td className="py-3 px-2 text-center">
                            <input
                              type="checkbox"
                              checked={teamStatusInputs[config.id]?.[teamStanding.team_id]?.qualified || false}
                              onChange={(e) => {
                                setTeamStatusInputs(prev => ({
                                  ...prev,
                                  [config.id]: {
                                    ...prev[config.id],
                                    [teamStanding.team_id]: {
                                      ...prev[config.id]?.[teamStanding.team_id],
                                      qualified: e.target.checked,
                                      winner: prev[config.id]?.[teamStanding.team_id]?.winner || false,
                                      knockedOut: prev[config.id]?.[teamStanding.team_id]?.knockedOut || false
                                    }
                                  }
                                }));
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="py-3 px-2 text-center">
                            <input
                              type="checkbox"
                              checked={teamStatusInputs[config.id]?.[teamStanding.team_id]?.winner || false}
                              onChange={(e) => {
                                setTeamStatusInputs(prev => ({
                                  ...prev,
                                  [config.id]: {
                                    ...prev[config.id],
                                    [teamStanding.team_id]: {
                                      ...prev[config.id]?.[teamStanding.team_id],
                                      qualified: prev[config.id]?.[teamStanding.team_id]?.qualified || false,
                                      winner: e.target.checked,
                                      knockedOut: prev[config.id]?.[teamStanding.team_id]?.knockedOut || false
                                    }
                                  }
                                }));
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="py-3 px-2 text-center">
                            <input
                              type="checkbox"
                              checked={teamStatusInputs[config.id]?.[teamStanding.team_id]?.knockedOut || false}
                              onChange={(e) => {
                                setTeamStatusInputs(prev => ({
                                  ...prev,
                                  [config.id]: {
                                    ...prev[config.id],
                                    [teamStanding.team_id]: {
                                      ...prev[config.id]?.[teamStanding.team_id],
                                      qualified: prev[config.id]?.[teamStanding.team_id]?.qualified || false,
                                      winner: prev[config.id]?.[teamStanding.team_id]?.winner || false,
                                      knockedOut: e.target.checked
                                    }
                                  }
                                }));
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}

        {/* Config Rules */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center space-x-2 mb-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h6 className="text-sm font-semibold text-blue-900">Config Rules</h6>
          </div>
          <div className="text-sm text-blue-800 space-y-1">
            {(() => {
              const rules = generateTieBreakerRules(config);
              return (
                <>
                  <p>• {rules.pointsText}</p>
                  <p>• {rules.tieBreakerText}</p>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Knockout Matches Table Component
interface KnockoutMatchesTableProps {
  tournamentId: string;
  seasonId?: string;
  config: TournamentStandingsConfig;
  knockoutInputs: {[matchId: string]: {level: number, position: number}};
  setKnockoutInputs: React.Dispatch<React.SetStateAction<{[matchId: string]: {level: number, position: number}}>>;
  teamStatusInputs: {[configId: string]: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}}};
  setTeamStatusInputs: React.Dispatch<React.SetStateAction<{[configId: string]: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}}}>>;
  refreshTrigger: number;
}

const KnockoutMatchesTable: React.FC<KnockoutMatchesTableProps> = ({ 
  tournamentId, 
  seasonId, 
  config,
  knockoutInputs,
  setKnockoutInputs,
  teamStatusInputs,
  setTeamStatusInputs,
  refreshTrigger
}) => {
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch team standings data for checkbox states
  const [teamStandingsData, setTeamStandingsData] = useState<any[]>([]);
  const [teamStandingsLoading, setTeamStandingsLoading] = useState(true);
  
  // Fetch knockout matches data for sync check
  const [knockoutMatchesData, setKnockoutMatchesData] = useState<any[]>([]);
  
  useEffect(() => {
    const fetchTeamStandings = async () => {
      if (!config.id) return;
      
      try {
        setTeamStandingsLoading(true);
        const { data, error } = await supabase
          .from('team_standings')
          .select(`
            *,
            teams!inner(name)
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true);
        
        if (error) {
          console.error('Error fetching team standings:', error);
          return;
        }
        
        console.log('🔍 Team Standings Debug - Config ID:', config.id);
        console.log('🔍 Team Standings Data:', data);
        console.log('🔍 Team Standings Count:', data?.length);
        
        // Check for duplicate teams in the same config
        if (data && data.length > 0) {
          const teamIds = data.map(team => team.team_id);
          const uniqueTeamIds = [...new Set(teamIds)];
          console.log('🔍 Unique Team IDs:', uniqueTeamIds);
          console.log('🔍 Total Teams:', teamIds.length, 'Unique Teams:', uniqueTeamIds.length);
          
          if (teamIds.length !== uniqueTeamIds.length) {
            console.warn('⚠️ Duplicate teams found in team_standings for config:', config.id);
            data.forEach(team => {
              console.log(`Team ${team.team_id}: P=${team.matches_played}, Pts=${team.total_points}`);
            });
          }
        }
        
        // Handle duplicate teams by keeping only the most recent record
        let processedData = data || [];
        if (processedData.length > 0) {
          const teamMap = new Map();
          processedData.forEach(team => {
            const existingTeam = teamMap.get(team.team_id);
            if (!existingTeam || new Date(team.created_at || team.updated_at) > new Date(existingTeam.created_at || existingTeam.updated_at)) {
              teamMap.set(team.team_id, team);
            }
          });
          processedData = Array.from(teamMap.values());
          console.log('🔍 Processed Team Standings (deduplicated):', processedData.length, 'teams');
        }
        
        setTeamStandingsData(processedData);
        
        // Initialize teamStatusInputs from database
        if (data && data.length > 0) {
          const initialStatusInputs: {[teamId: string]: {qualified: boolean, winner: boolean, knockedOut: boolean}} = {};
          data.forEach((team: any) => {
            initialStatusInputs[team.team_id] = {
              qualified: team.is_qualified || false,
              winner: team.is_winner || false,
              knockedOut: team.is_knocked_out || false
            };
          });
          setTeamStatusInputs(prev => ({
            ...prev,
            [config.id]: initialStatusInputs
          }));
        }
      } catch (err) {
        console.error('Error fetching team standings:', err);
      } finally {
        setTeamStandingsLoading(false);
      }
    };
    
    fetchTeamStandings();
  }, [config.id, setTeamStatusInputs, refreshTrigger]);

  // Simple sync check - always show publish required if there are any UI changes
  const isDataSynced = () => {
    // If no team standings data exists, we need to publish
    if (!teamStandingsData || teamStandingsData.length === 0) {
      return false;
    }
    
    // Check if any checkbox states differ from database
    if (teamStatusInputs[config.id]) {
      for (const [teamId, inputs] of Object.entries(teamStatusInputs[config.id])) {
        const dbRecord = teamStandingsData.find(ts => ts.team_id === teamId);
        if (dbRecord) {
          if (
            dbRecord.is_qualified !== inputs.qualified ||
            dbRecord.is_winner !== inputs.winner ||
            dbRecord.is_knocked_out !== inputs.knockedOut
          ) {
            return false; // Found a difference, need to publish
          }
        }
      }
    }
    
    // Check if any knockout inputs differ from database
    for (const [matchId, inputs] of Object.entries(knockoutInputs)) {
      const dbRecord = knockoutMatchesData.find(km => km.match_id === matchId);
      if (dbRecord) {
        if (
          dbRecord.knockout_level !== inputs.level ||
          dbRecord.level_position !== inputs.position
        ) {
          return false; // Found a difference, need to publish
        }
      } else if (inputs.level && inputs.position) {
        // If we have UI inputs but no DB record, we need to publish
        return false;
      }
    }
    
    return true; // All data matches
  };

  // Fetch knockout matches data for sync check
  useEffect(() => {
    const fetchKnockoutMatchesData = async () => {
      if (!config.id) return;
      
      try {
        const { data, error } = await supabase
          .from('knockout_matches')
          .select('*')
          .eq('tournament_config_id', config.id)
          .eq('is_active', true);
        
        if (error) {
          console.error('Error fetching knockout matches:', error);
          return;
        }
        
        console.log('🔍 Knockout Matches Data:', data);
        setKnockoutMatchesData(data || []);
        
        // Initialize knockoutInputs from database
        if (data && data.length > 0) {
          const initialKnockoutInputs: {[matchId: string]: {level: number, position: number}} = {};
          data.forEach((match: any) => {
            initialKnockoutInputs[match.match_id] = {
              level: match.knockout_level || 1,
              position: match.level_position || 1
            };
          });
          setKnockoutInputs(prev => ({
            ...prev,
            ...initialKnockoutInputs
          }));
        }
      } catch (err) {
        console.error('Error fetching knockout matches:', err);
      }
    };
    
    fetchKnockoutMatchesData();
  }, [config.id, setKnockoutInputs, refreshTrigger]);

  useEffect(() => {
    const fetchKnockoutMatches = async () => {
      if (!tournamentId) return;

      try {
        setLoading(true);
        setError(null);

        // Get mapped match IDs from the config
        let mappedMatchIds: string[] = [];
        if (config.match_mapping) {
          if (typeof config.match_mapping === 'string') {
            try {
              mappedMatchIds = JSON.parse(config.match_mapping);
            } catch (e) {
              mappedMatchIds = [];
            }
          } else if (Array.isArray(config.match_mapping)) {
            mappedMatchIds = config.match_mapping;
          }
        }

        if (mappedMatchIds.length === 0) {
          setMatches([]);
          setLoading(false);
          return;
        }

        // Fetch match details for mapped matches
        const { data: matchData, error: matchError } = await supabase
          .from('matches')
          .select(`
            id,
            home_team_id,
            away_team_id,
            home_score,
            away_score,
            home_penalty_score,
            away_penalty_score,
            start_date,
            status
          `)
          .in('id', mappedMatchIds)
          .order('start_date', { ascending: true });

        if (matchError) {
          throw matchError;
        }

        // Fetch teams data
        const teamIds = [...new Set(matchData?.flatMap(match => [match.home_team_id, match.away_team_id]) || [])];
        const { data: teamsData, error: teamsError } = await supabase
          .from('teams')
          .select('id, name')
          .in('id', teamIds);

        if (teamsError) {
          throw teamsError;
        }

        // Create teams lookup map
        const teamsMap = new Map(teamsData?.map(team => [team.id, team.name]) || []);

        // Fetch match points for these matches to get detailed results
        const { data: matchPointsData, error: matchPointsError } = await supabase
          .from('match_points')
          .select(`
            match_id,
            team_id,
            result,
            points_awarded,
            goals_for,
            goals_against,
            goal_difference,
            teams!inner(name)
          `)
          .in('match_id', mappedMatchIds);

        if (matchPointsError) {
          throw matchPointsError;
        }

        // Combine match data with team names and match points
        const enrichedMatches = matchData?.map(match => {
          const matchPoints = matchPointsData?.filter(mp => mp.match_id === match.id) || [];
          return {
            ...match,
            home_team_name: teamsMap.get(match.home_team_id) || 'Unknown Team',
            away_team_name: teamsMap.get(match.away_team_id) || 'Unknown Team',
            match_points: matchPoints
          };
        }) || [];

        setMatches(enrichedMatches);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch knockout matches');
        console.error('KnockoutMatchesTable error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchKnockoutMatches();
  }, [tournamentId, seasonId, config.match_mapping]);

  if (loading || teamStandingsLoading) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-md font-semibold text-gray-900 mb-3">Knockout Matches</h5>
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading knockout matches...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-md font-semibold text-gray-900 mb-3">Knockout Matches</h5>
        <div className="text-center py-8">
          <div className="text-red-400 text-4xl mb-3">❌</div>
          <p className="text-red-600">Error loading knockout matches</p>
          <p className="text-sm text-red-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!matches || matches.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-md font-semibold text-gray-900 mb-3">Knockout Matches</h5>
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">🥊</div>
          <p className="text-gray-600">No knockout matches available</p>
          <p className="text-sm text-gray-500 mt-1">
            Map matches to this configuration to view knockout results
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h5 className="text-md font-semibold text-gray-900">Knockout Matches</h5>
        
        {/* Sync Status Message */}
        <div className="flex items-center space-x-2">
          {isDataSynced() ? (
            <div className="flex items-center space-x-1 text-green-600 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">All data synced</span>
            </div>
          ) : (
            <div className="flex items-center space-x-1 text-orange-600 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="font-medium">Publish required</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Matches List - Split into two cards per match */}
      <div className="space-y-4">
        {matches.map((match, index) => (
          <div key={match.id} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Card 1: Match Information (from match_points) */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 lg:col-span-2">
              {/* Header Row: Match Information and Date */}
              <div className="flex items-center justify-between mb-2">
                <h6 className="text-sm font-semibold text-gray-900">Match Information</h6>
                <span className="text-sm text-gray-500">
                  {new Date(match.start_date).toLocaleDateString()}
                </span>
              </div>
              
              {/* Score Row: Teams, Score, and Results */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">{match.home_team_name}</span>
                  <div className="flex items-center space-x-1.5">
                    {/* Penalty score in parentheses if exists */}
                    {match.home_penalty_score !== null && match.home_penalty_score > 0 && (
                      <span className="text-xs text-gray-500">({match.home_penalty_score})</span>
                    )}
                    <span className="text-base font-semibold text-gray-900">
                      {match.home_score !== null ? match.home_score : '-'}
                    </span>
                    <span className="text-sm text-gray-400">-</span>
                    <span className="text-base font-semibold text-gray-900">
                      {match.away_score !== null ? match.away_score : '-'}
                    </span>
                    {/* Penalty score in parentheses if exists */}
                    {match.away_penalty_score !== null && match.away_penalty_score > 0 && (
                      <span className="text-xs text-gray-500">({match.away_penalty_score})</span>
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700">{match.away_team_name}</span>
                </div>
                
                {/* Match Results (Win/Loss/Draw) */}
                {match.match_points && match.match_points.length > 0 && (
                  <div className="flex items-center space-x-6">
                    {/* Home team result */}
                    <div className="flex items-center space-x-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        match.match_points[0]?.result === 'win' ? 'bg-green-100 text-green-800' :
                        match.match_points[0]?.result === 'draw' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {match.match_points[0]?.result}
                      </span>
                      <span className="text-sm text-gray-600">{match.match_points[0]?.teams.name}</span>
                    </div>
                    
                    {/* Away team result */}
                    <div className="flex items-center space-x-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        match.match_points[1]?.result === 'win' ? 'bg-green-100 text-green-800' :
                        match.match_points[1]?.result === 'draw' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {match.match_points[1]?.result}
                      </span>
                      <span className="text-sm text-gray-600">{match.match_points[1]?.teams.name}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Card 2: Match Controls (Level & Position) */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 lg:col-span-1">
              <div className="mb-2">
                <h6 className="text-sm font-semibold text-gray-900">Match Controls</h6>
              </div>
              
              {/* Level & Position Inputs - Row 1 */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <label className="text-xs font-medium text-gray-700">Level:</label>
                  <input
                    type="number"
                    min="1"
                    value={knockoutInputs[match.id]?.level || ''}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      setKnockoutInputs(prev => ({
                        ...prev,
                        [match.id]: {
                          ...prev[match.id],
                          level: value,
                          position: prev[match.id]?.position || 1
                        }
                      }));
                    }}
                    className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="1"
                  />
                </div>
                <div className="flex items-center space-x-1">
                  <label className="text-xs font-medium text-gray-700">Position:</label>
                  <input
                    type="number"
                    min="1"
                    value={knockoutInputs[match.id]?.position || ''}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      setKnockoutInputs(prev => ({
                        ...prev,
                        [match.id]: {
                          ...prev[match.id],
                          level: prev[match.id]?.level || 1,
                          position: value
                        }
                      }));
                    }}
                    className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="1"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Table 1: Calculated Statistics from match_points (No Checkboxes) */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h6 className="text-sm font-semibold text-gray-900">Calculated Statistics</h6>
          <p className="text-xs text-gray-600 mt-1">Data calculated from match results</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GD</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(() => {
                // Calculate team statistics from matches
                const teamStats = new Map();
                
                matches.forEach(match => {
                  // Process home team
                  if (!teamStats.has(match.home_team_id)) {
                    teamStats.set(match.home_team_id, {
                      team_id: match.home_team_id,
                      team_name: match.home_team_name,
                      matches_played: 0,
                      wins: 0,
                      draws: 0,
                      losses: 0,
                      goals_for: 0,
                      goals_against: 0,
                      goal_difference: 0,
                      total_points: 0
                    });
                  }
                  
                  const homeTeam = teamStats.get(match.home_team_id);
                  homeTeam.matches_played++;
                  homeTeam.goals_for += match.home_score || 0;
                  homeTeam.goals_against += match.away_score || 0;
                  
                  // Determine result based on scores
                  if (match.home_score > match.away_score) {
                    homeTeam.wins++;
                    homeTeam.total_points += config.default_win_points || 0;
                  } else if (match.home_score < match.away_score) {
                    homeTeam.losses++;
                    homeTeam.total_points += config.default_loss_points || 0;
                  } else {
                    homeTeam.draws++;
                    homeTeam.total_points += config.default_draw_points || 0;
                  }
                  
                  // Process away team
                  if (!teamStats.has(match.away_team_id)) {
                    teamStats.set(match.away_team_id, {
                      team_id: match.away_team_id,
                      team_name: match.away_team_name,
                      matches_played: 0,
                      wins: 0,
                      draws: 0,
                      losses: 0,
                      goals_for: 0,
                      goals_against: 0,
                      goal_difference: 0,
                      total_points: 0
                    });
                  }
                  
                  const awayTeam = teamStats.get(match.away_team_id);
                  awayTeam.matches_played++;
                  awayTeam.goals_for += match.away_score || 0;
                  awayTeam.goals_against += match.home_score || 0;
                  
                  // Determine result based on scores
                  if (match.away_score > match.home_score) {
                    awayTeam.wins++;
                    awayTeam.total_points += config.default_win_points || 0;
                  } else if (match.away_score < match.home_score) {
                    awayTeam.losses++;
                    awayTeam.total_points += config.default_loss_points || 0;
                  } else {
                    awayTeam.draws++;
                    awayTeam.total_points += config.default_draw_points || 0;
                  }
                });
                
                // Calculate goal difference and sort by points, then goal difference
                const teams = Array.from(teamStats.values()).map(team => ({
                  ...team,
                  goal_difference: team.goals_for - team.goals_against
                })).sort((a, b) => {
                  if (b.total_points !== a.total_points) {
                    return b.total_points - a.total_points;
                  }
                  return b.goal_difference - a.goal_difference;
                });
                
                // Add position
                teams.forEach((team, index) => {
                  team.position = index + 1;
                });
                
                return teams.map((team) => (
                  <tr key={team.team_id} className="hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                        team.position === 1 ? 'bg-green-100 text-green-800' :
                        team.position === 2 ? 'bg-blue-100 text-blue-800' :
                        team.position === 3 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {team.position}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">{team.team_name}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                    <td className={`py-3 px-3 text-center font-medium ${
                      team.goal_difference > 0 ? 'text-green-600' :
                      team.goal_difference < 0 ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                      {team.goal_difference > 0 ? `+${team.goal_difference}` : team.goal_difference}
                    </td>
                    <td className="py-3 px-4 text-center font-bold text-gray-900">{team.total_points}</td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Table 2: Team Status from team_standings (With Checkboxes) */}
      {teamStandingsData.length > 0 && (
        <div className="mt-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h6 className="text-sm font-semibold text-gray-900">Team Status</h6>
            <p className="text-xs text-gray-600 mt-1">Data from team_standings table with status settings</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GD</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teamStandingsData
                  .filter(teamStanding => {
                    // Only show teams that are part of the current tournament configuration (in matches)
                    return matches.some(match => 
                      match.home_team_id === teamStanding.team_id || 
                      match.away_team_id === teamStanding.team_id
                    );
                  })
                  .map((teamStanding) => (
                  <tr key={teamStanding.team_id} className="hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                        teamStanding.position === 1 ? 'bg-green-100 text-green-800' :
                        teamStanding.position === 2 ? 'bg-blue-100 text-blue-800' :
                        teamStanding.position === 3 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {teamStanding.position}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">{teamStanding.teams?.name || 'Unknown Team'}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{teamStanding.matches_played}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{teamStanding.wins}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{teamStanding.draws}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{teamStanding.losses}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{teamStanding.goals_for}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{teamStanding.goals_against}</td>
                    <td className={`py-3 px-3 text-center font-medium ${
                      teamStanding.goal_difference > 0 ? 'text-green-600' :
                      teamStanding.goal_difference < 0 ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                      {teamStanding.goal_difference > 0 ? `+${teamStanding.goal_difference}` : teamStanding.goal_difference}
                    </td>
                    <td className="py-3 px-4 text-center font-bold text-gray-900">{teamStanding.total_points}</td>
                    <td className="py-3 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={teamStatusInputs[config.id]?.[teamStanding.team_id]?.qualified || false}
                        onChange={(e) => {
                          setTeamStatusInputs(prev => ({
                            ...prev,
                            [config.id]: {
                              ...prev[config.id],
                              [teamStanding.team_id]: {
                                ...prev[config.id]?.[teamStanding.team_id],
                                qualified: e.target.checked,
                                winner: prev[config.id]?.[teamStanding.team_id]?.winner || false,
                                knockedOut: prev[config.id]?.[teamStanding.team_id]?.knockedOut || false
                              }
                            }
                          }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="py-3 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={teamStatusInputs[config.id]?.[teamStanding.team_id]?.winner || false}
                        onChange={(e) => {
                          setTeamStatusInputs(prev => ({
                            ...prev,
                            [config.id]: {
                              ...prev[config.id],
                              [teamStanding.team_id]: {
                                ...prev[config.id]?.[teamStanding.team_id],
                                qualified: prev[config.id]?.[teamStanding.team_id]?.qualified || false,
                                winner: e.target.checked,
                                knockedOut: prev[config.id]?.[teamStanding.team_id]?.knockedOut || false
                              }
                            }
                          }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="py-3 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={teamStatusInputs[config.id]?.[teamStanding.team_id]?.knockedOut || false}
                        onChange={(e) => {
                          setTeamStatusInputs(prev => ({
                            ...prev,
                            [config.id]: {
                              ...prev[config.id],
                              [teamStanding.team_id]: {
                                ...prev[config.id]?.[teamStanding.team_id],
                                qualified: prev[config.id]?.[teamStanding.team_id]?.qualified || false,
                                winner: prev[config.id]?.[teamStanding.team_id]?.winner || false,
                                knockedOut: e.target.checked
                              }
                            }
                          }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Config Rules - Simplified for knockout format */}
      <div className="mt-4 bg-blue-50 rounded-lg p-3 border border-blue-200">
        <div className="flex items-center space-x-2 mb-2">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h6 className="text-sm font-semibold text-blue-900">Config Rules</h6>
        </div>
        <div className="text-sm text-blue-800 space-y-1">
          {(() => {
            const rules = generateTieBreakerRules(config);
            return (
              <>
                <p>• {rules.pointsText}</p>
                <p>• {rules.tieBreakerText}</p>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

// League Standings Display Component (Read-only)
interface LeagueStandingsDisplayProps {
  tournamentId: string;
  seasonId?: string;
  config: TournamentStandingsConfig;
}

const LeagueStandingsDisplay: React.FC<LeagueStandingsDisplayProps> = ({ 
  tournamentId, 
  seasonId, 
  config
}) => {
  const [teamStandingsData, setTeamStandingsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchTeamStandings = async () => {
      if (!config.id) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('team_standings')
          .select(`
            *,
            teams!inner(name)
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true)
          .order('position', { ascending: true });
        
        if (error) {
          console.error('Error fetching team standings:', error);
          return;
        }
        
        setTeamStandingsData(data || []);
      } catch (error) {
        console.error('Error fetching team standings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamStandings();
  }, [config.id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading league standings...</p>
        </div>
      </div>
    );
  }

  if (teamStandingsData.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">📊</div>
          <p className="text-gray-600">No standings data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Publish team standings from the Points tab to view data here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h6 className="text-sm font-semibold text-gray-900">League Standings</h6>
          <p className="text-xs text-gray-600 mt-1">Final standings from team_standings table</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">GD</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {teamStandingsData.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="py-3 px-3">
                    <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                      team.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                      team.position === 2 ? 'bg-gray-100 text-gray-800' :
                      team.position === 3 ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {team.position}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    {team.teams?.name || 'Unknown Team'}
                  </td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                  <td className="py-3 px-4 text-center text-gray-600">{team.goal_difference}</td>
                  <td className="py-3 px-4 text-center font-semibold text-gray-900">{team.total_points}</td>
                  <td className="py-3 px-2 text-center">
                    {team.is_qualified ? (
                      <span className="text-green-600 text-lg">✅</span>
                    ) : (
                      <span className="text-gray-300 text-lg">☐</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {team.is_winner ? (
                      <span className="text-yellow-600 text-lg">🏆</span>
                    ) : (
                      <span className="text-gray-300 text-lg">☐</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {team.is_knocked_out ? (
                      <span className="text-red-600 text-lg">❌</span>
                    ) : (
                      <span className="text-gray-300 text-lg">☐</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Knockout Standings Display Component (Read-only)
interface KnockoutStandingsDisplayProps {
  tournamentId: string;
  seasonId?: string;
  config: TournamentStandingsConfig;
}

const KnockoutStandingsDisplay: React.FC<KnockoutStandingsDisplayProps> = ({ 
  tournamentId, 
  seasonId, 
  config
}) => {
  const [knockoutMatchesData, setKnockoutMatchesData] = useState<any[]>([]);
  const [teamStandingsData, setTeamStandingsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!config.id) return;
      
      try {
        setLoading(true);
        
        // Fetch knockout matches
        console.log('🔍 Fetching knockout matches for config:', config.id);
        
        // Fetch knockout matches with proper joins
        const { data: knockoutMatches, error: knockoutError } = await supabase
          .from('knockout_matches')
          .select(`
            *,
            matches(
              id,
              home_team_id,
              away_team_id,
              start_date,
              home_score,
              away_score,
              home_penalty_score,
              away_penalty_score,
              home_teams:teams!home_team_id(
                id,
                name,
                short_name,
                logo
              ),
              away_teams:teams!away_team_id(
                id,
                name,
                short_name,
                logo
              )
            )
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true)
          .order('knockout_level', { ascending: true })
          .order('level_position', { ascending: true });
        
        console.log('🔍 Knockout matches with joins:', knockoutMatches);
        console.log('🔍 Knockout error:', knockoutError);
        
        if (knockoutError) {
          console.error('Error fetching knockout matches:', knockoutError);
          console.log('🔍 Trying fallback query...');
          
          // Fallback: Simple query without joins
          const { data: fallbackData, error: fallbackError } = await supabase
            .from('knockout_matches')
            .select('*')
            .eq('tournament_config_id', config.id)
            .eq('is_active', true)
            .order('knockout_level', { ascending: true })
            .order('level_position', { ascending: true });
          
          console.log('🔍 Fallback knockout matches:', fallbackData);
          console.log('🔍 Fallback error:', fallbackError);
          
          if (fallbackError) {
            console.error('Fallback query also failed:', fallbackError);
            setKnockoutMatchesData([]);
          } else if (fallbackData && fallbackData.length > 0) {
            console.log('🔍 Using fallback data:', fallbackData?.length || 0, 'matches');
            
            // Fetch match and team data for fallback
            const matchIds = fallbackData.map(km => km.match_id);
            console.log('🔍 Fetching match data for IDs:', matchIds);
            
            const { data: matchesData, error: matchesError } = await supabase
              .from('matches')
              .select(`
                id,
                home_team_id,
                away_team_id,
                start_date,
                home_score,
                away_score,
                home_penalty_score,
                away_penalty_score
              `)
              .in('id', matchIds);
            
            console.log('🔍 Matches data:', matchesData);
            console.log('🔍 Expected match IDs:', matchIds);
            console.log('🔍 Found match IDs:', matchesData?.map(m => m.id) || []);
            console.log('🔍 Missing match IDs:', matchIds.filter(id => !matchesData?.find(m => m.id === id)));
            
            if (matchesError) {
              console.error('Error fetching matches:', matchesError);
              setKnockoutMatchesData([]);
            } else if (matchesData && matchesData.length > 0) {
              // Get team IDs
              const teamIds = new Set();
              matchesData.forEach(match => {
                if (match.home_team_id) teamIds.add(match.home_team_id);
                if (match.away_team_id) teamIds.add(match.away_team_id);
              });
              
              console.log('🔍 Team IDs to fetch:', Array.from(teamIds));
              
              // Fetch teams data
              const { data: teamsData, error: teamsError } = await supabase
                .from('teams')
                .select('id, name, short_name, logo')
                .in('id', Array.from(teamIds));
              
              console.log('🔍 Teams data:', teamsData);
              
              if (teamsError) {
                console.error('Error fetching teams:', teamsError);
                setKnockoutMatchesData([]);
              } else {
                // Combine knockout matches with match and team data
                const combinedData = fallbackData.map(km => {
                  const matchData = matchesData?.find(m => m.id === km.match_id);
                  if (matchData) {
                    const homeTeam = teamsData?.find(t => t.id === matchData.home_team_id);
                    const awayTeam = teamsData?.find(t => t.id === matchData.away_team_id);
                    
                    return {
                      ...km,
                      matches: {
                        ...matchData,
                        home_teams: homeTeam,
                        away_teams: awayTeam
                      }
                    };
                  }
                  return km;
                });
                
                console.log('🔍 Combined fallback data:', combinedData);
                setKnockoutMatchesData(combinedData);
              }
            } else {
              console.log('🔍 No matches data found for fallback');
              setKnockoutMatchesData([]);
            }
          } else {
            console.log('🔍 No fallback data found');
            setKnockoutMatchesData([]);
          }
        } else {
          console.log('🔍 Setting knockout matches data:', knockoutMatches?.length || 0, 'matches');
          setKnockoutMatchesData(knockoutMatches || []);
        }
        
        // Fetch team standings
        const { data: teamData, error: teamError } = await supabase
          .from('team_standings')
          .select(`
            *,
            teams!inner(name)
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true)
          .order('position', { ascending: true });
        
        if (teamError) {
          console.error('Error fetching team standings:', teamError);
        } else {
          setTeamStandingsData(teamData || []);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [config.id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading knockout standings...</p>
        </div>
      </div>
    );
  }

  if (knockoutMatchesData.length === 0 && teamStandingsData.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">🏆</div>
          <p className="text-gray-600">No knockout data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Publish knockout matches from the Points tab to view data here
          </p>
          <div className="mt-4 p-4 bg-gray-100 rounded-lg text-left">
            <p className="text-sm text-gray-600 mb-2">Debug Info:</p>
            <p className="text-xs text-gray-500">Config ID: {config.id}</p>
            <p className="text-xs text-gray-500">Knockout Matches Count: {knockoutMatchesData.length}</p>
            <p className="text-xs text-gray-500">Team Standings Count: {teamStandingsData.length}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show knockout bracket even if we only have basic knockout match data
  if (knockoutMatchesData.length === 0 && teamStandingsData.length > 0) {
    return (
      <div className="p-6 space-y-6">
        {/* Show team standings */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h6 className="text-sm font-semibold text-gray-900">Team Status</h6>
            <p className="text-xs text-gray-600 mt-1">Final team standings and status</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">GD</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teamStandingsData.map((team) => (
                  <tr key={team.id} className="hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                        team.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                        team.position === 2 ? 'bg-gray-100 text-gray-800' :
                        team.position === 3 ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {team.position}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">
                      {team.teams?.name || 'Unknown Team'}
                    </td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{team.goal_difference}</td>
                    <td className="py-3 px-2 text-center">
                      {team.is_qualified ? (
                        <span className="text-green-600 text-lg">✅</span>
                      ) : (
                        <span className="text-gray-300 text-lg">☐</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {team.is_winner ? (
                        <span className="text-yellow-600 text-lg">🏆</span>
                      ) : (
                        <span className="text-gray-300 text-lg">☐</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {team.is_knocked_out ? (
                        <span className="text-red-600 text-lg">❌</span>
                      ) : (
                        <span className="text-gray-300 text-lg">☐</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Debug info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800 mb-2">⚠️ Knockout Bracket Not Available</p>
          <p className="text-xs text-yellow-700">Config ID: {config.id}</p>
          <p className="text-xs text-yellow-700">Knockout Matches: {knockoutMatchesData.length}</p>
          <p className="text-xs text-yellow-700">Team Standings: {teamStandingsData.length}</p>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('🔍 KnockoutStandingsDisplay - knockoutMatchesData:', knockoutMatchesData);
  console.log('🔍 KnockoutStandingsDisplay - teamStandingsData:', teamStandingsData);
  console.log('🔍 KnockoutStandingsDisplay - knockoutMatchesData length:', knockoutMatchesData.length);
  
  // Show a test bracket even with minimal data
  if (knockoutMatchesData.length > 0) {
    console.log('🔍 First knockout match:', knockoutMatchesData[0]);
    console.log('🔍 Match data for first match:', knockoutMatchesData[0]?.matches);
  }

  // Group matches by knockout level and sort by level_position
  const matchesByLevel = knockoutMatchesData.reduce((acc, match) => {
    const level = match.knockout_level;
    if (!acc[level]) acc[level] = [];
    acc[level].push(match);
    return acc;
  }, {} as {[key: number]: any[]});

  console.log('🔍 Matches grouped by level:', matchesByLevel);

  // Sort matches within each level by level_position
  Object.keys(matchesByLevel).forEach(level => {
    matchesByLevel[parseInt(level)].sort((a: any, b: any) => a.level_position - b.level_position);
  });

  // Create bracket structure - smaller knockout_level means earlier round
  const sortedLevels = Object.keys(matchesByLevel)
    .map(level => parseInt(level))
    .sort((a, b) => a - b);

  return (
    <div className="p-6 space-y-6">
      {/* Knockout Bracket */}
      {Object.keys(matchesByLevel).length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h6 className="text-sm font-semibold text-gray-900">Knockout Bracket</h6>
            <p className="text-xs text-gray-600 mt-1">Tournament progression from early rounds to finals</p>
          </div>
          <div className="p-6">
            {/* Create a proper bracket layout */}
            <div className="flex justify-center overflow-x-auto">
              <div className="flex space-x-12 min-w-max">
                {sortedLevels.map((level, levelIndex) => (
                  <div key={level} className="flex-shrink-0">
                    {/* Level Header - Removed static names */}
                    <div className="text-center mb-6">
                      <div className="w-40 h-0.5 bg-blue-300 mx-auto"></div>
                    </div>
                    
                    {/* Matches in this level */}
                    <div className="space-y-4">
                        {matchesByLevel[level].map((match: any, matchIndex: number) => {
                          console.log('🔍 Match data for rendering:', match.matches);
                          console.log('🔍 Raw scores - Home:', match.matches?.home_score, 'Away:', match.matches?.away_score);
                          console.log('🔍 Penalty scores - Home:', match.matches?.home_penalty_score, 'Away:', match.matches?.away_penalty_score);
                          console.log('🔍 Score types - Home:', typeof match.matches?.home_score, 'Away:', typeof match.matches?.away_score);
                          return (
                          <div key={match.id} className="relative" style={{ marginTop: `${(level - 1) * 20}px` }}>
                            {/* Match Card */}
                            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border-2 border-blue-300 w-52 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-blue-400 relative overflow-hidden">
                              {/* Blue accent bar at top */}
                              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>
                              
                              {/* Teams and Score */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                  <div className="flex items-center space-x-2 flex-1">
                                    {match.matches?.home_teams?.logo && (
                                      <img 
                                        src={match.matches.home_teams.logo} 
                                        alt={match.matches.home_teams.name}
                                        className="w-6 h-6 rounded-full object-cover"
                                      />
                                    )}
                                    <span className="font-medium text-gray-900 truncate">
                                      {match.matches?.home_teams?.short_name || match.matches?.home_teams?.name || `Team ${match.matches?.home_team_id || 'A'}`}
                                    </span>
                                  </div>
                                  <span className="font-bold text-gray-800 text-lg">
                                    {match.matches?.home_score ?? '?'}
                                    {match.matches?.home_penalty_score > 0 && (
                                      <span className="text-xs text-gray-600 ml-1">
                                        ({match.matches.home_penalty_score})
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                  <div className="flex items-center space-x-2 flex-1">
                                    {match.matches?.away_teams?.logo && (
                                      <img 
                                        src={match.matches.away_teams.logo} 
                                        alt={match.matches.away_teams.name}
                                        className="w-6 h-6 rounded-full object-cover"
                                      />
                                    )}
                                    <span className="font-medium text-gray-900 truncate">
                                      {match.matches?.away_teams?.short_name || match.matches?.away_teams?.name || `Team ${match.matches?.away_team_id || 'B'}`}
                                    </span>
                                  </div>
                                  <span className="font-bold text-gray-800 text-lg">
                                    {match.matches?.away_score ?? '?'}
                                    {match.matches?.away_penalty_score > 0 && (
                                      <span className="text-xs text-gray-600 ml-1">
                                        ({match.matches.away_penalty_score})
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Match Date */}
                              {match.matches?.start_date && (
                                <div className="text-center mt-3 pt-2 border-t border-blue-200">
                                  <span className="text-xs text-gray-600">
                                    {new Date(match.matches.start_date).toLocaleDateString()}
                                  </span>
                                </div>
                              )}
                            </div>
                          
                          {/* Connection Lines to Next Level */}
                          {levelIndex < sortedLevels.length - 1 && (
                            <>
                              {/* Horizontal line */}
                              <div className="absolute top-1/2 -right-6 w-6 h-0.5 bg-gray-800 transform -translate-y-1/2"></div>
                              {/* Vertical line */}
                              <div className="absolute top-1/2 -right-6 w-0.5 h-8 bg-gray-800 transform -translate-y-1/2"></div>
                            </>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Status */}
      {teamStandingsData.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h6 className="text-sm font-semibold text-gray-900">Team Status</h6>
            <p className="text-xs text-gray-600 mt-1">Final team standings and status</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                  <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">GD</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                  <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teamStandingsData.map((team) => (
                  <tr key={team.id} className="hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                        team.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                        team.position === 2 ? 'bg-gray-100 text-gray-800' :
                        team.position === 3 ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {team.position}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">
                      {team.teams?.name || 'Unknown Team'}
                    </td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                    <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{team.goal_difference}</td>
                    <td className="py-3 px-2 text-center">
                      {team.is_qualified ? (
                        <span className="text-green-600 text-lg">✅</span>
                      ) : (
                        <span className="text-gray-300 text-lg">☐</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {team.is_winner ? (
                        <span className="text-yellow-600 text-lg">🏆</span>
                      ) : (
                        <span className="text-gray-300 text-lg">☐</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {team.is_knocked_out ? (
                        <span className="text-red-600 text-lg">❌</span>
                      ) : (
                        <span className="text-gray-300 text-lg">☐</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Group Standings Display Component (Read-only)
interface GroupStandingsDisplayProps {
  tournamentId: string;
  seasonId?: string;
  config: TournamentStandingsConfig;
}

const GroupStandingsDisplay: React.FC<GroupStandingsDisplayProps> = ({ 
  tournamentId, 
  seasonId, 
  config
}) => {
  const [teamStandingsData, setTeamStandingsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchTeamStandings = async () => {
      if (!config.id) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('team_standings')
          .select(`
            *,
            teams!inner(name)
          `)
          .eq('tournament_config_id', config.id)
          .eq('is_active', true)
          .order('position', { ascending: true });
        
        if (error) {
          console.error('Error fetching team standings:', error);
          return;
        }
        
        setTeamStandingsData(data || []);
      } catch (error) {
        console.error('Error fetching team standings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamStandings();
  }, [config.id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading group standings...</p>
        </div>
      </div>
    );
  }

  if (teamStandingsData.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">📊</div>
          <p className="text-gray-600">No group standings data available</p>
          <p className="text-sm text-gray-500 mt-1">
            Publish group standings from the Points tab to view data here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h6 className="text-sm font-semibold text-gray-900">Group Stage Standings</h6>
          <p className="text-xs text-gray-600 mt-1">Final standings from team_standings table</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 font-medium text-gray-600">Pos</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Team</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">P</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">W</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">D</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">L</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GF</th>
                <th className="text-center py-3 px-3 font-medium text-gray-600">GA</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">GD</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Pts</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">QF ✅</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">W 🏆</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">KO ❌</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {teamStandingsData.map((team) => (
                <tr key={team.id} className="hover:bg-gray-50">
                  <td className="py-3 px-3">
                    <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${
                      team.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                      team.position === 2 ? 'bg-gray-100 text-gray-800' :
                      team.position === 3 ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {team.position}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    {team.teams?.name || 'Unknown Team'}
                  </td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.matches_played}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.wins}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.draws}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.losses}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.goals_for}</td>
                  <td className="py-3 px-3 text-center text-gray-600">{team.goals_against}</td>
                  <td className="py-3 px-4 text-center text-gray-600">{team.goal_difference}</td>
                  <td className="py-3 px-4 text-center font-semibold text-gray-900">{team.total_points}</td>
                  <td className="py-3 px-2 text-center">
                    {team.is_qualified ? (
                      <span className="text-green-600 text-lg">✅</span>
                    ) : (
                      <span className="text-gray-300 text-lg">☐</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {team.is_winner ? (
                      <span className="text-yellow-600 text-lg">🏆</span>
                    ) : (
                      <span className="text-gray-300 text-lg">☐</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {team.is_knocked_out ? (
                      <span className="text-red-600 text-lg">❌</span>
                    ) : (
                      <span className="text-gray-300 text-lg">☐</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TournamentDetailPage;
